import { useState, useCallback } from 'react';
import { MATCH_META_API } from '../config/endpoints';

/**
 * Hook for fetching match metadata/statistics by video filename
 * @returns {Object} { fetchMatchMeta, loading, error }
 */
const useMatchMeta = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Fetch match metadata for a video file
     * @param {string} videoFileName - The video filename (e.g., "Prisha_0024_Near.mp4")
     * @returns {Promise<Object|null>} Match metadata or null on error
     */
    const fetchMatchMeta = useCallback(async (videoFileName) => {
        if (!videoFileName) {
            console.error('[useMatchMeta] Video filename is required');
            return null;
        }

        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('BQS_TOKEN');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            const url = `${MATCH_META_API}?videoFile=${encodeURIComponent(videoFileName)}`;
            console.log('[useMatchMeta] Fetching match metadata from:', url);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error('Match metadata not found for this video');
                } else if (response.status === 401) {
                    throw new Error('Authentication failed. Please log in again.');
                }
                throw new Error(`Failed to fetch match metadata: ${response.status}`);
            }

            const responseData = await response.json();
            console.log('[useMatchMeta] Received response:', responseData);

            // Handle response structure (assuming similar to rally API: { code, message, data })
            const matchMeta = responseData.data || responseData;
            console.log('[useMatchMeta] Match metadata:', matchMeta);

            return matchMeta;

        } catch (err) {
            console.error('[useMatchMeta] Error fetching match metadata:', err);
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        fetchMatchMeta,
        loading,
        error
    };
};

export default useMatchMeta;